import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  TrendUp, TrendDown, Minus, Sparkle, Star, Smiley, UsersFour,
  CaretDown, CalendarBlank, ArrowsLeftRight,
} from "@phosphor-icons/react";
import { LineChart, Line, Area, PieChart, Pie, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";
import useSiteContent from "../../lib/useSiteContent";
import { formatDate } from "../../lib/date";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "../ui/dropdown-menu";
import { CsDashboardDemographics, DemoTable, Heatmap } from "./CsDashboardDemographics";
import { CsDashboardRatings, RatingsSummaryGrid, RankedList } from "./CsDashboardRatings";
import { CsDashboardTrends, CsDashboardResponses, CsDashboardAlerts, ResponseFunnelCard, AlertsList } from "./CsDashboardMisc";

// oodel's real brand accent is GREEN (#16A34A — same hex as the `mango` /
// `.brand-green` utility used everywhere else in this app). Do not use orange.
export const ACCENT = "#16A34A";
export const CHART_COLORS = [ACCENT, "#0F172A", "#64748B", "#94A3B8", "#CBD5E1"];

// Shared "site-content-or-fallback" reader for Business Dashboard copy —
// every string using this is admin-editable via Site Content → "Customer
// Sentiment / SCORE" → "Business Dashboard — page text" (English only, no
// Urdu — this dashboard is business/admin-facing, not the public form).
export const dT = (content, key, fallback) => (content[key] && String(content[key]).trim()) ? content[key] : fallback;

export const PRESETS = [
  { label: "7D", days: 7, text: "Last 7 days" },
  { label: "30D", days: 30, text: "Last 30 days" },
  { label: "90D", days: 90, text: "Last 90 days" },
  { label: "6M", days: 182, text: "Last 6 months" },
  { label: "1Y", days: 365, text: "Last year" },
  { label: "All", days: 3650, text: "All time" },
];

const TABS = ["Overview", "Demographics", "Ratings", "Trends", "Insights", "Responses", "Alerts"];
// Internal tab identifiers (used for state/routing) stay fixed in English —
// only the DISPLAYED label is admin-editable, via these CMS keys.
const TAB_LABEL_KEYS = {
  Overview: ["cs_dash_tab_overview", "Overview"],
  Demographics: ["cs_dash_tab_demographics", "Demographics"],
  Ratings: ["cs_dash_tab_ratings", "Ratings"],
  Trends: ["cs_dash_tab_trends", "Trends"],
  Insights: ["cs_dash_tab_insights", "Insights"],
  Responses: ["cs_dash_tab_responses", "Responses"],
  Alerts: ["cs_dash_tab_alerts", "Alerts"],
};

/** Matches the exact stylized treatment used for this same "oodel x" feature
 * name in the Admin sidebar (Admin.jsx ADMIN_NAV) — small superscript "x". */
export const OodelX = ({ className = "" }) => (
  <span className={className}>oodel <sup className="text-[10px] -top-0.5 relative">x</sup></span>
);

export function TrendPill({ value, size = "sm" }) {
  if (value == null) return <span className="text-slate-400 text-xs">—</span>;
  const Icon = value > 0 ? TrendUp : value < 0 ? TrendDown : Minus;
  const cls = value > 0 ? "text-emerald-600 bg-emerald-50" : value < 0 ? "text-red-600 bg-red-50" : "text-slate-500 bg-slate-100";
  return (
    <span className={`inline-flex items-center gap-1 font-bold rounded-full px-2 py-0.5 ${size === "sm" ? "text-[11px]" : "text-xs"} ${cls}`}>
      <Icon size={size === "sm" ? 11 : 13} weight="bold" /> {value > 0 ? "+" : ""}{value}
    </span>
  );
}

export function Sparkline({ series }) {
  const rows = (series || []).map((s, i) => ({ i, avg: s.avg }));
  if (rows.every((r) => r.avg == null)) return <span className="text-slate-300 text-xs">—</span>;
  return (
    <div style={{ width: 140, height: 36 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={rows} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
          <defs>
            <linearGradient id="sparklineFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={ACCENT} stopOpacity={0.18} />
              <stop offset="100%" stopColor={ACCENT} stopOpacity={0} />
            </linearGradient>
          </defs>
          <YAxis domain={[1, 5]} hide />
          <Area type="monotone" dataKey="avg" stroke="none" fill="url(#sparklineFill)" connectNulls isAnimationActive={false} />
          <Line type="monotone" dataKey="avg" stroke={ACCENT} strokeWidth={2} dot={{ r: 2.5, fill: ACCENT, strokeWidth: 0 }} connectNulls isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// centerValue/centerSub render an overlaid total in the donut's hole, and the
// legend shows each slice's % share (with raw count alongside) — matching
// the "24 Responses" / "52%" treatment used across the reference design.
function DonutChart({ items, testid, centerValue, centerSub }) {
  const rows = items.filter((i) => i.n > 0);
  if (rows.length === 0) return <div className="text-xs text-slate-400 text-center py-8">No data yet</div>;
  const total = rows.reduce((s, r) => s + r.n, 0);
  return (
    <div className="flex items-center gap-4" data-testid={testid}>
      <div className="relative shrink-0" style={{ width: 96, height: 96 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={rows} dataKey="n" nameKey="label" innerRadius={30} outerRadius={46} paddingAngle={2}>
              {rows.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
        {centerValue != null && (
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <div className="text-base font-bold text-slate-900 leading-none">{centerValue}</div>
            {centerSub && <div className="text-[8px] text-slate-400 mt-0.5 text-center leading-tight">{centerSub}</div>}
          </div>
        )}
      </div>
      <div className="space-y-1 text-xs">
        {rows.map((r, i) => (
          <div key={r.label} className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full shrink-0" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
            <span className="text-slate-500">{r.label}</span>
            <span className="font-bold text-slate-800">{Math.round((r.n / total) * 100)}%</span>
            <span className="text-slate-400">({r.n})</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const KEY_INSIGHT_ICON = {
  trend: { Icon: Smiley, bg: "bg-emerald-50", color: ACCENT },
  weak_question: { Icon: Star, bg: "bg-amber-50", color: "#D97706" },
  top_segment: { Icon: UsersFour, bg: "bg-blue-50", color: "#2563EB" },
  weak_segment: { Icon: UsersFour, bg: "bg-rose-50", color: "#E11D48" },
  most_improved: { Icon: TrendUp, bg: "bg-teal-50", color: "#0D9488" },
};

/** Renders text with any standalone occurrence of the word "score" (any
 * casing, e.g. "SCORE", "Score") forced to lowercase + bold — matches the
 * "oodel score" brand treatment used across the business dashboard. */
function withScoreBold(text) {
  if (!text) return text;
  const parts = text.split(/(score)/i);
  return parts.map((p, i) => (p.toLowerCase() === "score" ? <b key={i} className="lowercase">score</b> : p));
}

function KeyInsightsRow({ insights }) {
  if (!insights.length) return <div className="text-sm text-slate-400">Not enough data yet for key insights.</div>;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="cs-key-insights">
      {insights.map((k, i) => {
        const { Icon, bg, color } = KEY_INSIGHT_ICON[k.type] || KEY_INSIGHT_ICON.trend;
        const showSigned = k.type === "trend" || k.type === "most_improved";
        return (
          <div key={i} className="bg-white rounded-lg border border-slate-200 shadow-sm p-4" data-testid={`cs-key-insight-${i}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-3 ${bg}`}><Icon size={16} weight="fill" style={{ color }} /></div>
            <div className="text-xs text-slate-500 leading-snug mb-1.5">{withScoreBold(k.label)}</div>
            {k.detail && <div className="text-[11px] font-medium text-slate-700 mb-1">{k.detail}</div>}
            <div className={`text-lg font-bold ${k.positive ? "text-emerald-600" : "text-amber-600"}`}>
              {showSigned ? `${k.value > 0 ? "+" : ""}${k.value}` : k.value}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** A single "highlight" card — a punchy, standalone, scannable fact from
 * the AI's `highlights[]` array. Color/icon are driven by the backend's
 * `positive` flag (true=good news, false=bad news, null=neutral), never by
 * regex-guessing at the text like the old sentence-splitting approach did. */
function HighlightCard({ h, i }) {
  const tone = h.positive === true ? "up" : h.positive === false ? "down" : "neutral";
  const Icon = tone === "up" ? TrendUp : tone === "down" ? TrendDown : Sparkle;
  const color = tone === "up" ? ACCENT : tone === "down" ? "#E11D48" : "#64748B";
  const bg = tone === "up" ? "bg-emerald-50" : tone === "down" ? "bg-rose-50" : "bg-slate-50";
  return (
    <div className={`flex items-start gap-2 text-sm text-slate-700 leading-snug rounded-lg p-3 ${bg}`} data-testid={`cs-ai-insight-card-${i}`}>
      <Icon size={14} weight="fill" className="shrink-0 mt-0.5" style={{ color }} />
      <span>{h.label}</span>
    </div>
  );
}

function AIInsightsGrid({ aiInsight, emptyText, isAdmin }) {
  const highlights = aiInsight?.highlights || [];
  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5" data-testid="cs-ai-insight-strip">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800"><Sparkle size={15} weight="fill" style={{ color: ACCENT }} /> <OodelX /> Insights</div>
        {!isAdmin && (
          <Link to="/business/oodel-x" className="text-xs font-semibold" style={{ color: ACCENT }} data-testid="cs-ai-insight-full-timeline-link">
            See full weekly &amp; monthly history →
          </Link>
        )}
      </div>
      {!aiInsight ? (
        // Nothing has ever been generated for this business/branch/week yet
        // (e.g. brand new, or under the response threshold) — the ONLY case
        // this generic line should cover.
        <p className="text-sm text-slate-400" data-testid="cs-ai-insight-empty">{emptyText}</p>
      ) : highlights.length === 0 ? (
        // A report DID run for this period — it just found nothing notable
        // (e.g. zero feedback that week). Show its own explanation instead
        // of the generic "not generated yet" line, which would wrongly
        // imply the system hasn't started.
        <p className="text-sm text-slate-500" data-testid="cs-ai-insight-quiet">{aiInsight.summary || emptyText}</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {highlights.map((h, i) => <HighlightCard key={i} h={h} i={i} />)}
        </div>
      )}
    </div>
  );
}

function OverviewTab({ data, isAdmin, onViewTab }) {
  const sc = data.score_card;
  const content = useSiteContent();
  const genderDonutRows = data.demographics.by_gender.map((g) => ({ label: g.label, n: g.n }));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5" data-testid="cs-score-card">
          <div className="text-sm font-semibold text-slate-800">oodel <span className="lowercase font-bold">score</span></div>
          <div className="text-xs text-slate-400 mt-0.5">{dT(content, "cs_dash_score_subtitle", "Sentiment Captured On Real Experiences")}</div>
          <div className="flex items-baseline gap-2 mt-3">
            <Star size={26} weight="fill" style={{ color: ACCENT }} />
            <div className="text-4xl sm:text-5xl font-bold tracking-tighter text-slate-900">{sc.avg_stars ?? "—"}</div>
            <div className="text-slate-400 text-sm">/ 5</div>
          </div>
          <div className="flex items-center gap-2 mt-3"><TrendPill value={sc.trend_delta} size="lg" /><span className="text-xs text-slate-400">{dT(content, "cs_dash_score_vs_previous", "vs previous period")}</span></div>
          <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-100 flex-wrap">
            <span className="text-xs bg-slate-100 rounded-full px-2.5 py-1 font-semibold text-slate-600">{sc.total_responses} responses</span>
            {sc.pct_positive != null && <span className="text-xs bg-emerald-50 text-emerald-700 rounded-full px-2.5 py-1 font-semibold">{sc.pct_positive}% positive (4★ &amp; 5★)</span>}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5" data-testid="cs-score-trend">
          <div className="text-sm font-medium text-slate-700 mb-2">{dT(content, "cs_dash_score_trend_title", "Score Trend")}</div>
          <div style={{ height: 180 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.score_trend} margin={{ top: 5, right: 8, bottom: 0, left: -20 }}>
                <CartesianGrid stroke="#F1F5F9" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                <YAxis domain={[0, 5]} tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} width={24} />
                <Tooltip />
                <Line type="monotone" dataKey="avg" stroke={ACCENT} strokeWidth={2} dot={{ r: 3 }} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5" data-testid="cs-score-distribution">
          <div className="text-sm font-medium text-slate-700 mb-3">{dT(content, "cs_dash_score_distribution_title", "Score Distribution")}</div>
          <DonutChart
            items={data.distribution.map((d) => ({ label: `${d.stars}★`, n: d.n }))}
            testid="cs-distribution-donut" centerValue={sc.total_responses} centerSub="Responses"
          />
        </div>
      </div>

      <div>
        <div className="text-sm font-semibold text-slate-800 mb-3">{dT(content, "cs_dash_key_insights_title", "This Month's Key Insights")}</div>
        <KeyInsightsRow insights={data.key_insights} />
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold text-slate-800">{dT(content, "cs_dash_ratings_summary_title", "Ratings Summary (by Question)")}</div>
          <button onClick={() => onViewTab("Ratings")} className="text-xs font-semibold shrink-0" style={{ color: ACCENT }} data-testid="cs-view-all-questions">{dT(content, "cs_dash_view_all_questions", "View all questions →")}</button>
        </div>
        <RatingsSummaryGrid rows={data.ratings_by_question} testid="cs-overview-ratings-summary" />
        <p className="text-[11px] text-slate-400 mt-2">{dT(content, "cs_dash_ratings_delta_caption", "↑↓ Change compared to previous period")}</p>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold text-slate-800">{dT(content, "cs_dash_demographics_breakdown_title", "Demographics Breakdown")}</div>
          <button onClick={() => onViewTab("Demographics")} className="text-xs font-semibold rounded-lg px-3 py-1.5 shrink-0" style={{ color: "white", background: ACCENT }} data-testid="cs-view-heatmap-btn">{dT(content, "cs_dash_view_heatmap_btn", "View Heatmap")}</button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <DemoTable title={dT(content, "cs_dash_demo_title_age", "By Age")} rows={data.demographics.by_age_band} testid="cs-overview-demo-age" showTrend={false} />
          <DemoTable
            title={dT(content, "cs_dash_demo_title_gender", "By Gender")} rows={data.demographics.by_gender} testid="cs-overview-demo-gender" showTrend={false}
            extra={<DonutChart items={genderDonutRows} testid="cs-overview-gender-donut" centerValue={sc.total_responses} centerSub="Responses" />}
          />
          <DemoTable title={dT(content, "cs_dash_demo_title_location", "By Location")} rows={data.by_location} testid="cs-overview-demo-location" showTrend={false} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5" data-testid="cs-overview-heatmap">
          <div className="text-sm font-medium text-slate-700 mb-1">{dT(content, "cs_dash_heatmap_title", "Segment Analysis Heatmap")}</div>
          <p className="text-xs text-slate-400 mb-4">{dT(content, "cs_dash_heatmap_subtitle", "Average score by age band × gender × city — greener is better.")}</p>
          <Heatmap heatmap={data.heatmap} />
        </div>
        <CsDashboardTrends data={data} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <RankedList title={dT(content, "cs_dash_top_strengths_title", "Top Strengths")} rows={data.top_strengths} tone="up" testid="cs-overview-top-strengths" />
        <RankedList title={dT(content, "cs_dash_top_opportunities_title", "Top Opportunities")} rows={data.top_opportunities} tone="down" testid="cs-overview-top-opportunities" />
        <ResponseFunnelCard data={data} isAdmin={isAdmin} testid="cs-overview-funnel" title={dT(content, "cs_dash_funnel_title", "Response Funnel")} />
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-4" data-testid="cs-overview-alerts">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-medium text-slate-700">{dT(content, "cs_dash_alerts_title", "Alerts")}</div>
            <button onClick={() => onViewTab("Alerts")} className="text-xs font-semibold" style={{ color: ACCENT }} data-testid="cs-view-all-alerts">{dT(content, "cs_dash_view_all_alerts", "View all")}</button>
          </div>
          <AlertsList alerts={data.alerts} limit={3} testid="cs-overview-alert" />
        </div>
      </div>
    </div>
  );
}

function InsightsTab({ data, isAdmin }) {
  const content = useSiteContent();
  const noInsightText = dT(content, "cs_no_ai_insight_text", "No insight generated yet — check back soon.");
  return (
    <div className="space-y-5">
      <AIInsightsGrid aiInsight={data.ai_insight} emptyText={noInsightText} isAdmin={isAdmin} />
      {data.ai_insight?.highlights?.length > 0 && data.ai_insight?.summary && (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5" data-testid="cs-insights-full-summary">
          <div className="text-sm font-medium text-slate-700 mb-2">{dT(content, "cs_dash_full_summary_title", "Consultant's Take")}</div>
          <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{data.ai_insight.summary}</p>
          <p className="text-[11px] text-slate-400 mt-3">Generated {formatDate(data.ai_insight.generated_at)}</p>
        </div>
      )}
      {data.ai_insight?.per_question_demographic_skew?.length > 0 && (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5" data-testid="cs-insights-demographic-skew">
          <div className="text-sm font-medium text-slate-700 mb-3">Who's saying what</div>
          <div className="space-y-2">
            {data.ai_insight.per_question_demographic_skew.map((d, i) => (
              <div key={i} className="flex items-start gap-2 text-sm text-slate-600 leading-snug" data-testid={`cs-demo-skew-row-${i}`}>
                {d.diff > 0 ? <TrendUp size={14} weight="fill" className="shrink-0 mt-0.5 text-emerald-500" /> : <TrendDown size={14} weight="fill" className="shrink-0 mt-0.5 text-rose-500" />}
                <span><strong>{d.segment}</strong> rate "{d.question}" {d.diff > 0 ? "higher" : "lower"} than average ({d.avg} vs {d.question_overall} overall, n={d.n})</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function CsDashboard({ isAdmin = false, businessId = null }) {
  const [searchParams] = useSearchParams();
  const [preset, setPreset] = useState("6M");
  // A deep-link from an Oodel X insight card ("View these exact responses")
  // carries its own since/until/tab/location_id — takes over from the
  // preset dropdown entirely until the admin/business picks a preset
  // themselves, so the numbers they land on always match the insight
  // they clicked through from. Watches `searchParams` directly (not just
  // on mount) because on the admin detail page this link navigates
  // within the SAME route — only the query string changes, so the
  // component never remounts.
  const [customRange, setCustomRange] = useState(null);
  const [tab, setTab] = useState("Overview");
  const [data, setData] = useState(null);
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState(null);
  const content = useSiteContent();
  const rootRef = useRef(null);
  const prevSearchKeyRef = useRef(null);

  useEffect(() => {
    const key = searchParams.toString();
    const since = searchParams.get("since"), until = searchParams.get("until");
    if (since && until) {
      setCustomRange({ since, until });
      const loc = searchParams.get("location_id");
      if (loc) setLocationId(loc);
      // Only scroll+toast on an ACTUAL query-string transition (not on
      // React StrictMode's double-invocation of this same effect during
      // initial mount, which would otherwise re-trigger it with an
      // unchanged key and fire an unwanted toast on a plain page load
      // that already carries these params, e.g. a bookmarked/shared
      // deep-link URL).
      if (prevSearchKeyRef.current !== null && prevSearchKeyRef.current !== key) {
        rootRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        toast.success(`Filtered to ${formatDate(since)} – ${formatDate(until)}`);
      }
    }
    const t = searchParams.get("tab");
    if (TABS.includes(t)) setTab(t);
    prevSearchKeyRef.current = key;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    const url = isAdmin ? `/admin/cs/businesses/${businessId}/locations` : "/business/me/locations";
    api.get(url).then((r) => setLocations(r.data)).catch(() => {});
  }, [isAdmin, businessId]);

  useEffect(() => {
    let params;
    if (customRange) {
      params = { since: customRange.since, until: customRange.until };
    } else {
      const p = PRESETS.find((x) => x.label === preset) || PRESETS[3];
      const until = new Date();
      const since = new Date(until.getTime() - p.days * 86400000);
      params = { since: since.toISOString().slice(0, 10), until: until.toISOString().slice(0, 10) };
    }
    if (locationId) params.location_id = locationId;
    const url = isAdmin ? `/admin/cs/businesses/${businessId}/dashboard` : "/business/me/dashboard";
    let stale = false;
    api.get(url, { params }).then((r) => { if (!stale) setData(r.data); }).catch(() => { if (!stale) toast.error("Failed to load dashboard"); });
    return () => { stale = true; }; // an in-flight request from a since-changed customRange/preset must never clobber a newer one's response
  }, [preset, customRange, isAdmin, businessId, locationId, tab]); // refetch on every tab switch too — otherwise a newly-approved Oodel X insight (or anything else that changed server-side after the initial load) stays stale until a hard page refresh

  const choosePreset = (label) => { setCustomRange(null); setPreset(label); };

  if (!data) return <div className="text-sm text-slate-400 py-12" data-testid="cs-dashboard-loading">Loading dashboard…</div>;

  const rangeLabel = formatDate(data.since) + " – " + formatDate(data.until);
  const allBranchesLabel = dT(content, "cs_dash_branch_all_label", "All branches");

  return (
    <div ref={rootRef} data-testid="cs-dashboard" style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900" data-testid="cs-dashboard-title">{dT(content, "cs_dash_title", "Dashboard Overview")}</h1>
        <div className="flex items-center gap-2 flex-wrap" data-testid="cs-dashboard-controls">
          {locations.length > 1 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 bg-white border border-slate-200 rounded-lg px-3 py-2 hover:bg-slate-50" data-testid="cs-branch-trigger">
                  {locationId ? (locations.find((l) => l.id === locationId)?.name || "Branch") : allBranchesLabel} <CaretDown size={11} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setLocationId(null)} data-testid="cs-branch-all" className={!locationId ? "font-bold" : ""}>
                  {!locationId ? "✓ " : ""}{allBranchesLabel}
                </DropdownMenuItem>
                {locations.map((l) => (
                  <DropdownMenuItem key={l.id} onClick={() => setLocationId(l.id)} data-testid={`cs-branch-${l.id}`} className={locationId === l.id ? "font-bold" : ""}>
                    {locationId === l.id ? "✓ " : ""}{l.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 bg-white border border-slate-200 rounded-lg px-3 py-2 hover:bg-slate-50" data-testid="cs-daterange-trigger">
                <CalendarBlank size={13} /> {rangeLabel} <CaretDown size={11} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {PRESETS.map((p) => (
                <DropdownMenuItem key={p.label} onClick={() => choosePreset(p.label)} data-testid={`cs-preset-${p.label.toLowerCase()}`} className={preset === p.label ? "font-bold" : ""}>
                  {preset === p.label ? "✓ " : ""}{p.text}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 bg-white border border-slate-200 rounded-lg px-3 py-2" data-testid="cs-compare-static" title="Every trend/delta on this dashboard already compares against the previous equivalent period">
            <ArrowsLeftRight size={13} /> {dT(content, "cs_dash_compare_label", "vs. previous period")}
          </div>
        </div>
      </div>

      <div className="flex gap-1 overflow-x-auto border-b border-slate-200 mt-4 mb-5" data-testid="cs-dashboard-tabs">
        {TABS.map((t) => {
          const [labelKey, labelFallback] = TAB_LABEL_KEYS[t];
          return (
            <button
              key={t} onClick={() => setTab(t)} data-testid={`tab-${t.toLowerCase()}`}
              className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === t ? "text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}
              style={tab === t ? { borderColor: ACCENT } : {}}
            >
              {dT(content, labelKey, labelFallback)}
              {t === "Alerts" && data.alerts.length > 0 && (
                <span className="bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{data.alerts.length}</span>
              )}
            </button>
          );
        })}
      </div>

      <div>
        {tab === "Overview" && <OverviewTab data={data} isAdmin={isAdmin} onViewTab={setTab} />}
        {tab === "Demographics" && <CsDashboardDemographics data={data} />}
        {tab === "Ratings" && <CsDashboardRatings data={data} />}
        {tab === "Trends" && <CsDashboardTrends data={data} />}
        {tab === "Insights" && <InsightsTab data={data} isAdmin={isAdmin} />}
        {tab === "Responses" && <CsDashboardResponses data={data} isAdmin={isAdmin} />}
        {tab === "Alerts" && <CsDashboardAlerts data={data} isAdmin={isAdmin} />}
      </div>

      <div className="flex flex-col sm:flex-row items-center justify-between gap-2 mt-8 pt-4 border-t border-slate-100 text-[11px] text-slate-400" data-testid="cs-dashboard-footer">
        <span>{dT(content, "cs_dash_footer_copyright", "© {year} Oodel. All rights reserved.").replace("{year}", new Date().getFullYear())}</span>
        <span>oodel <span className="font-bold lowercase">score</span> {dT(content, "cs_dash_footer_tagline", "is a proprietary metric of Oodel.")}</span>
        {content.cs_dash_footer_learn_more_enabled !== "false" && (
          <Link to="/oodelscore" className="font-semibold" style={{ color: ACCENT }} data-testid="cs-learn-more-link">{dT(content, "cs_dash_footer_learn_more", "Learn more about")} <span className="font-bold lowercase">score</span> →</Link>
        )}
      </div>
    </div>
  );
}
